import React from 'react';
import toast from 'react-hot-toast';
import styled from 'styled-components';

import { Button } from '../../../components/ui/Button';
import { Modal } from '../../../components/ui/Modal';
import {
  useCreateQrShare,
  useQrShareScopePreview,
} from '../../../hooks/useApi';
import type { DomainShareScope, QrShareDetail } from '../../../types/api';
import { buildDefaultShareName, getShareScopeConfig } from '../lib/shareScopes';
import { QrShareResultPanel } from './QrShareResultPanel';
import { ShareScopeSelector } from './ShareScopeSelector';

const Step = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--space-md);
`;

const Hint = styled.p`
  margin: 0;
  color: var(--text-muted);
  font-size: var(--font-size-sm);
  line-height: 1.5;
`;

const Field = styled.label`
  display: flex;
  flex-direction: column;
  gap: var(--space-1, 4px);
  color: var(--text-primary);
  font-size: var(--font-size-sm);
  font-weight: 600;
`;

const TextInput = styled.input`
  padding: var(--space-sm);
  border: 1px solid var(--border, #e5e7eb);
  border-radius: var(--radius-sm, 6px);
  background: var(--surface, #fff);
  color: var(--text-primary);
  font-size: var(--font-size-base);
  font-weight: 400;
`;

const Toggle = styled.label`
  display: flex;
  align-items: center;
  gap: var(--space-sm);
  color: var(--text-primary);
  font-size: var(--font-size-sm);
`;

const ErrorMessage = styled.p`
  margin: 0;
  color: var(--danger-600, #dc2626);
  font-size: var(--font-size-sm);
`;

const FooterActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: var(--space-xs);
`;

export interface CreateQrShareModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const CreateQrShareModal: React.FC<CreateQrShareModalProps> = ({
  isOpen,
  onClose,
}) => {
  const [scope, setScope] = React.useState<DomainShareScope | null>(null);
  const [name, setName] = React.useState('');
  const [expiresAt, setExpiresAt] = React.useState('');
  const [allowDownload, setAllowDownload] = React.useState(true);
  const [showRestaurantName, setShowRestaurantName] = React.useState(true);
  const [counts, setCounts] = React.useState<
    Partial<Record<DomainShareScope, number>>
  >({});
  const [created, setCreated] = React.useState<QrShareDetail | null>(null);

  const preview = useQrShareScopePreview(scope);
  const createShare = useCreateQrShare();

  const previewCount = preview.data?.file_count;
  const previewedScope = preview.data?.share_scope;

  React.useEffect(() => {
    if (
      previewedScope &&
      previewedScope !== 'custom' &&
      previewCount !== undefined
    ) {
      setCounts((current) => ({ ...current, [previewedScope]: previewCount }));
    }
  }, [previewedScope, previewCount]);

  const reset = React.useCallback(() => {
    setScope(null);
    setName('');
    setExpiresAt('');
    setAllowDownload(true);
    setShowRestaurantName(true);
    setCounts({});
    setCreated(null);
    createShare.reset();
  }, [createShare]);

  const handleClose = React.useCallback(() => {
    reset();
    onClose();
  }, [onClose, reset]);

  const handleScopeChange = React.useCallback((next: DomainShareScope) => {
    setScope(next);
    setName(buildDefaultShareName(next));
  }, []);

  const handleSubmit = React.useCallback(() => {
    if (!scope) return;

    createShare.mutate(
      {
        name: name.trim(),
        share_scope: scope,
        allow_download: allowDownload,
        show_restaurant_name: showRestaurantName,
        purpose: getShareScopeConfig(scope).defaultPurpose,
        // `exactOptionalPropertyTypes`: omit rather than send undefined.
        ...(expiresAt ? { expires_at: new Date(expiresAt).toISOString() } : {}),
      },
      {
        onSuccess: (share) => {
          setCreated(share);
          toast.success('QR creado');
        },
      },
    );
  }, [allowDownload, createShare, expiresAt, name, scope, showRestaurantName]);

  const scopeIsEmpty = scope !== null && counts[scope] === 0;
  const canSubmit =
    scope !== null &&
    name.trim().length > 0 &&
    name.trim().length <= 100 &&
    !scopeIsEmpty &&
    !createShare.isPending;

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={created ? 'QR listo para compartir' : 'Compartir por QR'}
      size="lg"
      footer={
        created ? (
          <FooterActions>
            <Button variant="primary" onClick={handleClose}>
              Hecho
            </Button>
          </FooterActions>
        ) : (
          <FooterActions>
            <Button variant="tertiary" onClick={handleClose}>
              Cancelar
            </Button>
            <Button
              variant="primary"
              onClick={handleSubmit}
              disabled={!canSubmit}
              loading={createShare.isPending}
            >
              Crear QR
            </Button>
          </FooterActions>
        )
      }
    >
      {created ? (
        <QrShareResultPanel share={created} />
      ) : (
        <Step>
          <ShareScopeSelector
            value={scope}
            counts={counts}
            onChange={handleScopeChange}
          />

          <Hint>
            Se compartirán todos los archivos actuales de esta categoría. Los
            archivos que subas después no se añadirán solos; puedes editar el
            paquete más adelante.
          </Hint>

          {scope && (
            <>
              <Field htmlFor="qr-share-name">
                Nombre del paquete
                <TextInput
                  id="qr-share-name"
                  value={name}
                  maxLength={100}
                  onChange={(event) => setName(event.target.value)}
                />
              </Field>

              <Field htmlFor="qr-share-expires">
                Vencimiento (opcional)
                <TextInput
                  id="qr-share-expires"
                  type="date"
                  value={expiresAt}
                  onChange={(event) => setExpiresAt(event.target.value)}
                />
              </Field>

              <Toggle>
                <input
                  type="checkbox"
                  checked={allowDownload}
                  onChange={(event) => setAllowDownload(event.target.checked)}
                />
                Permitir descargas
              </Toggle>

              <Toggle>
                <input
                  type="checkbox"
                  checked={showRestaurantName}
                  onChange={(event) =>
                    setShowRestaurantName(event.target.checked)
                  }
                />
                Mostrar nombre del restaurante
              </Toggle>
            </>
          )}

          {createShare.isError && (
            <ErrorMessage role="alert">
              {createShare.error instanceof Error
                ? createShare.error.message
                : 'No se pudo crear el QR.'}
            </ErrorMessage>
          )}
        </Step>
      )}
    </Modal>
  );
};
